/* eslint-disable no-magic-numbers */
import {
	OutcomeStatus,
	OutcomeStatusV2,
	OutcomeType,
	OutcomeVersion,
	CommitmentFilterType
} from "@teaming/common-enums";
import moment from "moment-timezone";

export const resolveOutcomeStatus = ( isCompleted, version ) => {
	if ( version === OutcomeVersion.V1 ) {
		return isCompleted ? OutcomeStatus.COMPLETED : OutcomeStatus.PLANNED;
	}
	if ( version === OutcomeVersion.V2 ) {
		return isCompleted ? OutcomeStatusV2.COMPLETE : OutcomeStatusV2.OUTSTANDING;
	}
	throw new Error( `invalid outcome version ${ version }; cannot determine outcome status` );
};

export const resolveCommitmentFilterStatus = c => {
	if ( c.completed ) {
		return [ CommitmentFilterType.COMPLETE, ];
	}

	const filterStatuses = [
		CommitmentFilterType.OUTSTANDING,
	];

	if ( !c.dueDate ) {
		filterStatuses.push( CommitmentFilterType.NO_DUE_DATE );
	} else {
		const now = moment();
		const startOfToday = now.clone().startOf( "day" );

		if ( c.dueDate.isBefore( startOfToday ) ) {
			filterStatuses.push( CommitmentFilterType.OVERDUE );
		}

		const endOfWeek = startOfToday.clone().add( 1, "weeks" );

		if ( c.dueDate.isSameOrAfter( startOfToday ) && c.dueDate.isBefore( endOfWeek ) ) {
			filterStatuses.push( CommitmentFilterType.DUE_IN_NEXT_WEEK );
		}
	}

	return filterStatuses;
};

export const toAPIOutcome = outcome => {
	return {
		id: outcome.id || undefined,
		topicId: outcome.topicId || undefined,
		type: outcome.type,
		completed: outcome.completed ?
			moment( outcome.completed ).format() :
			null,
		content: outcome.content.trim(),
		dueDate: outcome.dueDate ?
			moment( outcome.dueDate ).format() :
			null,
		assignments: outcome.assignments
			.filter( assignment => !!assignment?.assigned?.personId )
			.map( assignment => assignment.assigned.personId ),
		status: resolveOutcomeStatus( !!outcome.completed, outcome.outcomeVersion ),
		teamId: outcome.teamId,
	};
};

// team_outcome dates are stored as UTC in the DB, and returned as UTC in the API calls
// so to properly hydrate the moment instances, we treat them as UTC on the way in.
export const toClientOutcome = outcome => {
	const now = moment();
	const dueDate = outcome.dueDate ? moment.utc( outcome.dueDate ) : null;
	const clientOutcome = {
		...outcome,
		get createdDateStamp() {
			return this.created ?
				parseInt( this.created.format( "YYYYMMDDHHMMSS" ), 10 ) :
				null;
		},
		get dueDateStamp() {
			return this.dueDate ?
				parseInt( this.dueDate.format( "YYYYMMDDHHMMSS" ), 10 ) :
				null;
		},
		get completedDateStamp() {
			return this.completed ?
				parseInt( this.completed.format( "YYYYMMDDHHMMSS" ), 10 ) :
				null;
		},
		created: moment.utc( outcome.created ),
		dueDate,
		completed: outcome.completed ? moment.utc( outcome.completed ) : null,
		isOverdue: dueDate ? now.isAfter( dueDate ) : false,
		isCompleted: !!outcome.completed,
	};

	if ( [ OutcomeType.ACTION_ITEM, OutcomeType.PERSONAL_ACTION_ITEM, ].includes( clientOutcome.type ) ) {
		clientOutcome.matchedFilters = resolveCommitmentFilterStatus( clientOutcome );
	}

	return clientOutcome;
};
